/**
 * Onboarding pós-login — `Lumma Fit Onboarding.dc.html`.
 *
 * Só aparece depois do login e trata apenas de ajustes operacionais: o
 * cadastro inicial já foi feito no wizard.
 *
 * Diferença deliberada em relação ao protótipo: "Confirmar e-mail" não nasce
 * concluída. Ela reflete o estado real da conta, e o botão de ação dela
 * reenvia o e-mail. Mostrar como concluída algo que não aconteceu faria o
 * lojista descobrir na primeira emissão fiscal.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Faixa } from '../../componentes/Basicos'
import { Lockup } from '../../componentes/Marca'
import { api, ErroApi } from '../../plataforma/api'
import { useSessao } from '../../plataforma/contexto-sessao'

type Tarefa = {
  chave: string
  titulo: string
  descricao: string
  acao: string
  concluida: boolean
}

type Checklist = { tarefas: Tarefa[]; concluidas: number; total: number }

export function TelaOnboarding() {
  const navegar = useNavigate()
  const { situacao, revalidar } = useSessao()

  const [checklist, setChecklist] = useState<Checklist | null>(null)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')

  const carregar = useCallback(async (sinal?: AbortSignal) => {
    try {
      setChecklist(await api.get<Checklist>('/onboarding', sinal))
    } catch (problema) {
      if (problema instanceof DOMException && problema.name === 'AbortError') return
      setErro(mensagemDe(problema))
    }
  }, [])

  useEffect(() => {
    const controle = new AbortController()
    void carregar(controle.signal)
    return () => controle.abort()
  }, [carregar])

  const sessao = situacao.estado === 'autenticado' ? situacao.sessao : null
  const primeiroNome = sessao?.usuario.nome.split(' ')[0] ?? ''

  async function alternarTarefa(tarefa: Tarefa) {
    if (tarefa.chave === 'confirmar_email') {
      await reenviarConfirmacao()
      return
    }

    try {
      setChecklist(
        await api.put<Checklist>(`/onboarding/tarefas/${tarefa.chave}`, {
          concluida: !tarefa.concluida,
        }),
      )
      setErro('')
    } catch (problema) {
      setErro(mensagemDe(problema))
    }
  }

  async function reenviarConfirmacao() {
    if (!sessao) return
    if (sessao.email_confirmado) return

    try {
      await api.post('/email/reenvios', { email: sessao.usuario.email })
      setAviso('Reenviamos o e-mail de confirmação. Verifique a caixa de spam.')
      setErro('')
    } catch (problema) {
      setErro(mensagemDe(problema))
    }
  }

  const progresso = checklist && checklist.total > 0 ? checklist.concluidas / checklist.total : 0

  return (
    <div className="min-h-screen bg-superficie">
      <header className="flex h-[60px] items-center justify-between border-b border-divisoria px-8">
        <Lockup tamanhoLogo={28} tamanhoTexto={20} comSublabel={false} />
        <div className="flex items-center gap-3">
          <span className="text-[13px] text-texto-suave">{sessao?.loja.nome}</span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-marca-fundo text-xs font-bold text-marca">
            {sessao?.usuario.iniciais}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-[960px] px-8 pt-13 pb-20">
        <span className="inline-block rounded-full bg-marca-fundo px-3 py-[6px] text-[12.5px] font-semibold text-marca">
          Primeiro acesso
        </span>

        <h1 className="mt-4 font-titulo text-[32px] font-bold text-titulo">
          Bem-vinda, {primeiroNome}.
        </h1>
        <p className="mt-3 max-w-[560px] text-[15px] leading-[1.65] text-texto-suave">
          O cadastro inicial já foi feito na criação da conta. Faltam alguns ajustes operacionais
          para a loja vender hoje.
        </p>

        <div className="mt-6 flex max-w-[560px] items-center gap-4">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-divisoria">
            <div
              className="h-full rounded-full bg-marca transition-[width]"
              style={{ width: `${progresso * 100}%` }}
            />
          </div>
          <span className="tabular text-[13.5px] font-semibold text-texto">
            {checklist?.concluidas ?? 0} de {checklist?.total ?? 5}
          </span>
        </div>

        <div className="mt-8 grid items-start gap-6 lg:grid-cols-[1fr_292px]">
          <ul className="divide-y divide-divisoria rounded-onboarding border border-borda">
            {(checklist?.tarefas ?? []).map((tarefa) => (
              <li key={tarefa.chave} className="flex items-center gap-[14px] px-[22px] py-[18px]">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={tarefa.concluida}
                  aria-label={tarefa.titulo}
                  onClick={() => void alternarTarefa(tarefa)}
                  className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] text-[11px] font-bold text-white ${
                    tarefa.concluida ? 'border-ok bg-ok' : 'border-inativo bg-white'
                  }`}
                >
                  {tarefa.concluida ? '✓' : ''}
                </button>

                <span className="flex-1">
                  <span
                    className={`block text-[14.5px] font-semibold ${
                      tarefa.concluida ? 'text-texto-fraco line-through' : 'text-titulo'
                    }`}
                  >
                    {tarefa.titulo}
                  </span>
                  <span className="block text-[12.5px] text-texto-fraco">{tarefa.descricao}</span>
                </span>

                <button
                  type="button"
                  disabled={tarefa.concluida}
                  onClick={() => void alternarTarefa(tarefa)}
                  className={`shrink-0 rounded-sistema border px-[14px] py-[7px] text-[12.5px] font-semibold ${
                    tarefa.concluida
                      ? 'cursor-default border-divisoria text-texto-fraco'
                      : 'border-borda text-marca'
                  }`}
                >
                  {tarefa.concluida ? 'Concluído' : tarefa.acao}
                </button>
              </li>
            ))}
          </ul>

          <aside className="flex flex-col gap-4">
            <div className="rounded-onboarding border border-borda p-5">
              <h2 className="mb-2 font-titulo text-[15px] font-semibold text-titulo">
                Plano de avaliação
              </h2>
              <p className="text-[13px] leading-[1.6] text-texto-suave">
                {sessao?.loja.dias_restantes ?? 14} dias com todos os módulos liberados. Sem cartão
                de crédito.
              </p>
              <p className="mt-3 text-[12.5px] font-semibold text-marca">
                Restam {sessao?.loja.dias_restantes ?? 14} dias
              </p>
            </div>

            <div className="rounded-onboarding bg-marca-fundo p-5">
              <h2 className="mb-2 font-titulo text-[15px] font-semibold text-titulo">
                Migração de estoque
              </h2>
              <p className="text-[13px] leading-[1.6] text-texto-suave">
                Tem planilha ou outro sistema? A gente importa junto com você.
              </p>
              <a
                href="mailto:suporte@lummasoftware.com.br"
                className="mt-3 inline-block text-[13px] font-semibold text-marca hover:text-marca-forte"
              >
                Falar com o suporte
              </a>
            </div>

            <button
              type="button"
              onClick={() => {
                void revalidar()
                navegar('/app')
              }}
              className="text-[13px] font-semibold text-texto-fraco"
            >
              Ir direto para o dashboard
            </button>
          </aside>
        </div>

        {aviso && <Faixa tipo="sucesso">{aviso}</Faixa>}
        {erro && <Faixa tipo="erro">{erro}</Faixa>}
      </main>
    </div>
  )
}

function mensagemDe(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message
  return 'Não foi possível carregar o checklist.'
}
